import type { Countries } from "../catalogs/countries";
import type { DataTableInstance } from "../components/ui-kit/tables/data-table/table-features";

import { ppgConditionsText, ppgConditionText } from "../constants/enum-to-text";
import { dateTimeOrNull } from "./utils";

interface WithCountry {
  country?: string | null;
}

interface WithRegion extends WithCountry {
  region?: number | null;
}

interface WithPhysicians {
  physicians?: Array<{ uuid: string; full_name: string }> | null;
}

interface WithPpgRecording {
  uuid: string;
  measurement_datetime: string;
  measurement_condition: string;
}

interface WithAlgorithm {
  seerlinq_algorithm?: string | null;
  seerlinq_algorithm_version?: string | null;
}

function algorithmLabel<T extends WithAlgorithm>(item: T): string {
  return `${item.seerlinq_algorithm} ${item.seerlinq_algorithm_version}`;
}

/**
 * Combined "algorithm + version" multi-filter options for SQ-computed tables
 * (LVFP/DRI/AFib). Each option is the same `"<algorithm> <version>"` string the
 * cell displays. Pair with {@link algorithmPredicate} on the column's filter.
 */
export function algorithmOptions<T extends WithAlgorithm>(
  table: DataTableInstance<T>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const row of table.getCoreRowModel().rows) {
    if (row.original.seerlinq_algorithm == null) continue;
    const label = algorithmLabel(row.original);
    map.set(label, label);
  }
  return new Map([...map.entries()].sort(([, a], [, b]) => a.localeCompare(b)));
}

export function algorithmPredicate<T extends WithAlgorithm>(
  item: T,
  selected: unknown[],
): boolean {
  return selected.includes(algorithmLabel(item));
}

/**
 * Builds the Country multi-filter options from the rows currently in the
 * table — only values that actually occur in the data show up. Sorted
 * alphabetically by label.
 */
export function getCountryOptions<T extends WithCountry>(
  table: DataTableInstance<T>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const row of table.getCoreRowModel().rows) {
    const country = row.original.country;
    if (country) map.set(country, country);
  }
  return new Map([...map.entries()].sort(([, a], [, b]) => a.localeCompare(b)));
}

/**
 * Region multi-filter is keyed by composite "country:region" strings. The
 * available options are derived from whichever countries are currently
 * selected on the Country filter; when no country is selected the filter is
 * disabled (see {@link regionDisabled}).
 *
 * ISO 3166-1 alpha-2 country codes never contain ":", so splitting on ":" is
 * unambiguous.
 */
export function getRegionOptions<T extends WithRegion>(
  table: DataTableInstance<T>,
  countries: Countries,
): Map<string, string> {
  const selectedCountries =
    (table.getColumn("country")?.getFilterValue() as string[] | undefined) ??
    [];
  const multiCountry = selectedCountries.length > 1;
  const map = new Map<string, string>();
  for (const country of selectedCountries) {
    const countryName = countries.schemas[country]?.country_name ?? country;
    const countryRegions = countries.getRegionsMap(country);
    for (const [value, name] of countryRegions.entries()) {
      map.set(
        `${country}:${value}`,
        multiCountry ? `${countryName}: ${name}` : name,
      );
    }
  }
  return map;
}

export function regionDisabled<T extends WithRegion>(
  table: DataTableInstance<T>,
): boolean {
  const countries =
    (table.getColumn("country")?.getFilterValue() as string[] | undefined) ??
    [];
  return countries.length === 0;
}

export function regionPredicate<T extends WithRegion>(
  item: T,
  selected: unknown[],
): boolean {
  return selected.some((value) => {
    const [country, region] = String(value).split(":");
    return item.country === country && item.region === Number(region);
  });
}

/**
 * Generic multi-filter options: the distinct values of `attr` across the rows
 * currently loaded, labeled by their string form and sorted (numeric-aware,
 * so e.g. risk scores order 2 < 10).
 */
export function uniqueValueOptions<T>(
  attr: keyof T,
): (table: DataTableInstance<T>) => Map<unknown, string> {
  return (table) => {
    const map = new Map<unknown, string>();
    for (const row of table.getCoreRowModel().rows) {
      const value = row.original[attr];
      if (value == null || value === "") continue;
      map.set(value, String(value));
    }
    return new Map(
      [...map.entries()].sort(([, a], [, b]) =>
        a.localeCompare(b, undefined, { numeric: true }),
      ),
    );
  };
}

/**
 * Like {@link uniqueValueOptions}, but relabels PPG condition values with their
 * display text. The option *keys* stay the raw row values the rows are filtered
 * on — only the text the user reads changes. Relabels in place rather than
 * re-sorting, so the conditions keep the raw-value order `uniqueValueOptions`
 * gave them (their clinical 1/2/3 order) instead of going alphabetical by
 * translated label.
 */
export function ppgConditionValueOptions<T>(
  attr: keyof T,
): (table: DataTableInstance<T>) => Map<unknown, string> {
  const base = uniqueValueOptions<T>(attr);
  return (table) =>
    new Map(
      [...base(table)].map(([value, label]) => [
        value,
        // Both callers pass the array-valued `ppgConditions`; the PPG table's
        // scalar condition builds its options from `ppgConditionLabels`
        // directly. Anything non-array keeps the raw label as a fallback.
        Array.isArray(value) ? ppgConditionsText(value) : label,
      ]),
    );
}

/**
 * One option per PPG recording, keyed by its uuid and named by when and how it
 * was measured — what a person tells recordings apart by. Newest first, as the
 * PPG table itself opens.
 */
export function ppgRecordingOptions<T extends WithPpgRecording>(
  table: DataTableInstance<T>,
): Map<string, string> {
  return new Map(
    table
      .getCoreRowModel()
      .rows.map((row) => row.original)
      .sort((a, b) =>
        b.measurement_datetime.localeCompare(a.measurement_datetime),
      )
      .map((item) => [
        item.uuid,
        `${dateTimeOrNull(item.measurement_datetime)} · ${ppgConditionText(item.measurement_condition)}`,
      ]),
  );
}

/**
 * Multi-filter options for array-typed columns: the distinct values found
 * across all rows' arrays at `attr`, labeled by their string form and sorted
 * (numeric-aware). Pair with a column of `type: "array"` so the table uses the
 * `enumInArray` filter fn (matches a row when its array contains any selected
 * value).
 */
export function uniqueArrayValueOptions<T>(
  attr: keyof T,
): (table: DataTableInstance<T>) => Map<unknown, string> {
  return (table) => {
    const map = new Map<unknown, string>();
    for (const row of table.getCoreRowModel().rows) {
      const values = row.original[attr];
      if (!Array.isArray(values)) continue;
      for (const value of values) {
        if (value == null || value === "") continue;
        map.set(value, String(value));
      }
    }
    return new Map(
      [...map.entries()].sort(([, a], [, b]) =>
        a.localeCompare(b, undefined, { numeric: true }),
      ),
    );
  };
}

/**
 * Physician multi-filter: options come from physicians actually present on
 * the rows currently loaded, alphabetized by full_name.
 */
export function getPhysicianOptions<T extends WithPhysicians>(
  table: DataTableInstance<T>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const row of table.getCoreRowModel().rows) {
    for (const p of row.original.physicians ?? []) {
      map.set(p.uuid, p.full_name);
    }
  }
  return new Map([...map.entries()].sort(([, a], [, b]) => a.localeCompare(b)));
}

export function physicianPredicate<T extends WithPhysicians>(
  item: T,
  selected: unknown[],
): boolean {
  return item.physicians?.some((p) => selected.includes(p.uuid)) ?? false;
}
